import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import WeeklyCheckInFlow from './WeeklyCheckInFlow';
import './style.css';

const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

function WeeklyProgressIcon() {
  return (
    <svg className="weekly-progress-icon" viewBox="0 0 64 64" role="img" aria-hidden="true">
      <circle className="weekly-progress-track" cx="32" cy="32" r="26" />
      <circle className="weekly-progress-arc" cx="32" cy="32" r="26" />
      <circle className="weekly-progress-center" cx="32" cy="32" r="18" />
    </svg>
  );
}

export default function WeeklyCheckInCard({ onCompleted }) {
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [lastCheckIn, setLastCheckIn] = useState(null);
  const [flowOpen, setFlowOpen] = useState(false);

  const isDue = useMemo(() => {
    if (!userId) return false;
    if (!lastCheckIn) return true;
    const lastDate = new Date(lastCheckIn);
    if (Number.isNaN(lastDate.getTime())) return true;
    const nextDue = new Date(lastDate.getTime() + WEEK_IN_MS);
    return Date.now() >= nextDue.getTime();
  }, [lastCheckIn, userId]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        if (!mounted) return;
        const uid = auth?.user?.id || null;
        setUserId(uid);
        if (!uid) {
          setLastCheckIn(null);
          return;
        }
        const { data } = await supabase
          .from('weekly_checkins')
          .select('createdat')
          .eq('userid', uid)
          .order('createdat', { ascending: false })
          .limit(1)
          .maybeSingle();
        setLastCheckIn(data?.createdat || null);
      } catch (err) {
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const handleOpenFlow = () => setFlowOpen(true);
  const handleCloseFlow = () => setFlowOpen(false);
  const handleCompleted = (createdAt) => {
    if (createdAt) setLastCheckIn(createdAt);
    if (typeof onCompleted === 'function') onCompleted();
    setFlowOpen(false);
  };

  if (loading || !isDue) {
    return null;
  }

  return (
    <>
      <div className="weekly-checkin-card">
        <div className="weekly-checkin-content">
          <p className="weekly-label">Weekly Check-In</p>
          <h3>Ready for your weekly pulse check?</h3>
          <p>Answer a few quick questions so we can adjust your plan each week.</p>
          <button className="weekly-btn" onClick={handleOpenFlow}>Start Check-In</button>
          {lastCheckIn && (
            <span className="weekly-last">
              Last submitted {new Date(lastCheckIn).toLocaleDateString()}
            </span>
          )}
        </div>
        <div className="weekly-progress-shell">
          <WeeklyProgressIcon />
        </div>
      </div>
      <WeeklyCheckInFlow
        open={flowOpen}
        onClose={handleCloseFlow}
        onCompleted={handleCompleted}
        userId={userId}
      />
    </>
  );
}
